import React, { useState } from 'react';
import { Collapse, Tooltip } from '@mui/material';
import {
  ChevronDown,
  ChevronRight,
  EyeOff,
  FileText,
  Info,
  Layers,
  ListTree,
  MessageSquare,
  Paperclip,
  Sparkles,
  Wrench,
} from 'lucide-react';
import AnswerSources from '../AnswerSources';
import { useTheme } from '../../../context/ThemeContext';
import { n0, formatDurationMs, formatCost, RUN_TYPE_LABELS } from '../UsageAnalytics/usageFormat';
import {
  formatWhen,
  prettyMaybeJson,
  runStatus,
  runTitle,
  STEP_KIND_LABELS,
} from './runDetailFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './RunDetail.module.scss';

const StatusPill = ({ status }) => (
  <span
    className={`${sharedStyles.statusPill} ${
      status === 'error' ? sharedStyles.inactive : sharedStyles.active
    }`}
  >
    {status}
  </span>
);

/** One tool call — collapsed to name/status/duration, expanded to args + result. */
const ToolCallItem = ({ call, redacted }) => {
  const [open, setOpen] = useState(false);
  const failed = call?.status === 'error';
  const args = prettyMaybeJson(call?.args);
  const result = prettyMaybeJson(call?.result);
  const hasBody = Boolean(args || result) || redacted;

  return (
    <div className={styles.toolItem}>
      <button
        type="button"
        className={styles.toolHead}
        onClick={() => hasBody && setOpen((o) => !o)}
        aria-expanded={open}
        disabled={!hasBody}
      >
        <span className={failed ? styles.errDot : styles.okDot} aria-hidden />
        <span className={styles.toolName}>{call?.name || 'tool'}</span>
        {call?.member_id != null && (
          <span className={styles.toolMeta}>member {call.member_id}</span>
        )}
        <span className={styles.toolMeta}>{formatDurationMs(call?.duration_ms)}</span>
        {hasBody &&
          (open ? (
            <ChevronDown size={15} aria-hidden />
          ) : (
            <ChevronRight size={15} aria-hidden />
          ))}
      </button>
      {hasBody && (
        <Collapse in={open} unmountOnExit>
          <div className={styles.toolBody}>
            {redacted ? (
              <div className={styles.notice}>
                <EyeOff size={15} aria-hidden />
                <span>Arguments and result are hidden for your role.</span>
              </div>
            ) : (
              <>
                {args && (
                  <div>
                    <span className={styles.codeLabel}>Arguments</span>
                    <pre className={styles.codeBlock}>{args}</pre>
                  </div>
                )}
                {result && (
                  <div>
                    <span className={styles.codeLabel}>Result</span>
                    <pre className={styles.codeBlock}>{result}</pre>
                  </div>
                )}
              </>
            )}
          </div>
        </Collapse>
      )}
    </div>
  );
};

/**
 * The reviewable detail of one run: what was asked, what came back, which tools
 * ran, and the per-step breakdown for chains and teams.
 *
 * Two collections that must not be confused (and are therefore rendered as two
 * distinct sections with different affordances):
 *   - `children` — sub-runs recorded as their own rows (chain nodes, team HIL
 *     legs). They carry authoritative tokens/cost and link to their own detail.
 *   - `steps` — a DISPLAY breakdown (team members on a streaming run, batch-loop
 *     summary). A streaming team run records ONE row carrying the whole run's
 *     tokens; its members live in `steps` precisely so they aren't double
 *     counted. Never sum tokens from steps — so none are shown here.
 */
const RunDetailView = ({ detail, onOpenChild, redactedMessage = null }) => {
  const { isDarkMode } = useTheme();
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  if (!detail) return null;

  const run = detail.run || {};
  const redacted = Boolean(detail.content_redacted);
  const truncated = Boolean(detail.truncated);
  const status = runStatus(run);
  const toolCalls = Array.isArray(detail.tool_calls) ? detail.tool_calls : [];
  const steps = Array.isArray(detail.steps) ? detail.steps : [];
  const children = Array.isArray(detail.children) ? detail.children : [];
  const sources = Array.isArray(detail.sources) ? detail.sources : [];
  const cost = formatCost(run.estimated_cost, run.estimated_cost != null ? 100 : null);
  const transcript = detail.transcript;

  return (
    <>
      <div className={styles.pillRow}>
        <span className={styles.typePill}>
          {RUN_TYPE_LABELS[run.run_type] || run.run_type || 'run'}
        </span>
        <StatusPill status={status} />
        <span className={sharedStyles.muted}>{formatWhen(run.started_at)}</span>
        {run.session_id && (
          <Tooltip title="Session this run belongs to" arrow>
            <span className={sharedStyles.muted}>· session {run.session_id}</span>
          </Tooltip>
        )}
      </div>

      {run.error_message && (
        <div className={`${styles.notice} ${styles.noticeError}`}>
          <Info size={15} aria-hidden />
          <span>{run.error_message}</span>
        </div>
      )}

      <div className={styles.metricGrid}>
        <div className={styles.metricTile}>
          <span className={styles.metricLabel}>Duration</span>
          <span className={styles.metricValue}>{formatDurationMs(run.duration_ms)}</span>
        </div>
        <div className={styles.metricTile}>
          <span className={styles.metricLabel}>Tokens</span>
          <span className={styles.metricValue}>{n0(run.total_tokens)}</span>
          <span className={styles.metricSub}>
            in {n0(run.input_tokens)} · out {n0(run.output_tokens)}
          </span>
        </div>
        <div className={styles.metricTile}>
          <span className={styles.metricLabel}>Tool calls</span>
          <span className={styles.metricValue}>{n0(run.tool_calls)}</span>
        </div>
        <div className={styles.metricTile}>
          <span className={styles.metricLabel}>Est. cost</span>
          <span className={styles.metricValue}>{cost.text}</span>
        </div>
      </div>

      {/* Structure stays visible without `usage_run_content`; only text is gated.
          The partner surface passes its own wording (Partner tier guide §5.7). */}
      {redacted && (
        <div className={styles.notice}>
          <EyeOff size={15} aria-hidden />
          {redactedMessage ? (
            <span>{redactedMessage}</span>
          ) : (
            <span>
              <strong>Content hidden</strong> — you can see what ran, not what was said. Viewing
              prompts and answers needs the <code>usage_run_content</code> entitlement.
            </span>
          )}
        </div>
      )}

      {truncated && (
        <div className={`${styles.notice} ${styles.noticeWarn}`}>
          <Info size={15} aria-hidden />
          <span>Some fields hit their size cap and were truncated for storage.</span>
        </div>
      )}

      {!redacted && (
        <>
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              <MessageSquare size={13} aria-hidden />
              Input
            </h4>
            <div className={styles.textPane}>
              {detail.input_text ? (
                <>
                  {detail.input_text}
                  {truncated && <span className={styles.truncNote}> …truncated</span>}
                </>
              ) : (
                <span className={styles.textPaneEmpty}>No input recorded.</span>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              <Sparkles size={13} aria-hidden />
              Output
            </h4>
            <div className={styles.textPane}>
              {detail.output_text ? (
                <>
                  {detail.output_text}
                  {truncated && <span className={styles.truncNote}> …truncated</span>}
                </>
              ) : (
                <span className={styles.textPaneEmpty}>
                  {status === 'error' ? 'The run failed before producing an answer.' : 'No output recorded.'}
                </span>
              )}
            </div>
          </section>
        </>
      )}

      {toolCalls.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <Wrench size={13} aria-hidden />
            Tool calls ({toolCalls.length})
          </h4>
          {toolCalls.map((call, idx) => (
            <ToolCallItem key={`${call?.name || 'tool'}-${idx}`} call={call} redacted={redacted} />
          ))}
        </section>
      )}

      {children.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h4 className={styles.sectionTitle}>
              <ListTree size={13} aria-hidden />
              Sub-runs ({children.length})
            </h4>
            <span className={styles.sectionNote}>Own tokens and cost · open for full detail</span>
          </div>
          {children.map((child, idx) => {
            const childCost = formatCost(
              child.estimated_cost,
              child.estimated_cost != null ? 100 : null
            );
            const childStatus = runStatus(child);
            const openable = Boolean(child.run_id) && child.has_trace !== false;
            return (
              <div
                key={child.run_id || child.id || idx}
                className={`${styles.subRow} ${openable ? styles.subRowClickable : ''}`}
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={() => openable && onOpenChild?.(child.run_id, child)}
                onKeyDown={(e) => {
                  if (openable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onOpenChild?.(child.run_id, child);
                  }
                }}
              >
                <span className={styles.subIndex}>{idx + 1}</span>
                <div className={styles.subMain}>
                  <span className={styles.subTitle}>{runTitle(child)}</span>
                  <span className={styles.subMeta}>
                    {RUN_TYPE_LABELS[child.run_type] || child.run_type || 'run'} ·{' '}
                    {formatDurationMs(child.duration_ms)} · {n0(child.total_tokens)} tok ·{' '}
                    {childCost.text}
                  </span>
                </div>
                <StatusPill status={childStatus} />
                {openable ? (
                  <ChevronRight size={16} aria-hidden />
                ) : (
                  <span className={styles.noTraceTag}>No detail</span>
                )}
              </div>
            );
          })}
        </section>
      )}

      {steps.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h4 className={styles.sectionTitle}>
              <Layers size={13} aria-hidden />
              Steps ({steps.length})
            </h4>
            <span className={styles.sectionNote}>
              Breakdown only — tokens are accounted on the run above
            </span>
          </div>
          {steps.map((step, idx) => {
            const label =
              step?.name ||
              step?.member_name ||
              step?.agent_name ||
              step?.node_name ||
              STEP_KIND_LABELS[step?.kind] ||
              `Step ${idx + 1}`;
            const meta = [
              STEP_KIND_LABELS[step?.kind] || step?.kind,
              step?.member_id != null ? `member ${step.member_id}` : null,
              step?.duration_ms != null ? formatDurationMs(step.duration_ms) : null,
              step?.status || null,
            ]
              .filter(Boolean)
              .join(' · ');

            return (
              <div key={step?.id || `${label}-${idx}`} className={styles.subRow}>
                <span className={styles.subIndex}>{idx + 1}</span>
                <div className={styles.subMain}>
                  <span className={styles.subTitle}>{label}</span>
                  {meta && <span className={styles.subMeta}>{meta}</span>}
                  {!redacted && step?.output && <p className={styles.subOutput}>{step.output}</p>}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Per-record detail for a batch/loop job is not in `steps` by design. */}
      {run.run_type === 'batch_loop' && (
        <div className={styles.notice}>
          <Info size={15} aria-hidden />
          <span>
            Batch and loop jobs are recorded as one aggregate run. Per-record results live on the
            batch job itself, under Batch Jobs.
          </span>
        </div>
      )}

      {sources.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <FileText size={13} aria-hidden />
            Sources
          </h4>
          <AnswerSources sources={sources} isDarkMode={isDarkMode} />
        </section>
      )}

      {/* No persisted Agno session (normal with memory and learning both off) —
          the trace still renders, there is simply no transcript to show. */}
      {detail.transcript_available && transcript && (
        <section className={styles.section}>
          <button
            type="button"
            className={styles.crumbBtn}
            onClick={() => setTranscriptOpen((o) => !o)}
            aria-expanded={transcriptOpen}
          >
            {transcriptOpen ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
            Session transcript
            {transcript.message_count != null ? ` (${transcript.message_count} messages)` : ''}
          </button>
          <Collapse in={transcriptOpen} unmountOnExit>
            <div className={styles.section}>
              <span className={styles.sectionNote}>
                {[transcript.model, transcript.status].filter(Boolean).join(' · ')}
              </span>
              {transcript.content && <div className={styles.textPane}>{transcript.content}</div>}
              {Array.isArray(transcript.followups) && transcript.followups.length > 0 && (
                <div>
                  <span className={styles.codeLabel}>Follow-ups</span>
                  <pre className={styles.codeBlock}>{transcript.followups.join('\n')}</pre>
                </div>
              )}
            </div>
          </Collapse>
        </section>
      )}

      <div className={styles.notice}>
        <Paperclip size={15} aria-hidden />
        <span>
          Uploaded attachments aren't kept with the run, so a file this run referenced can't be
          shown here.
        </span>
      </div>
    </>
  );
};

export default RunDetailView;
